import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { AlertCircle } from 'lucide-react';
import { Switch } from '@/shared/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/shared/components/ui/alert';
import { translateDeep } from '@/shared/utils/i18nHelper';

type ParameterValue = string | number | boolean | Record<string, unknown> | null | undefined;

interface ParameterField {
  name: string;
  type: string;
  label: string;
  required: boolean;
  default?: ParameterValue;
  options?: Array<{ label: string; value: string }> | string[];
  value?: ParameterValue;
}

interface ParameterData {
  fields?: ParameterField[];
  values?: { [key: string]: ParameterValue };
  saveParameters?: (() => { success: boolean; skipped?: boolean; missingFields?: string[] } | false) | null;
}

interface ParametersProps {
  parameters: {
    fields: ParameterField[];
    values?: { [key: string]: ParameterValue };
  } | null | undefined;
  onUpdate: (parameters: ParameterData) => void;
  onChangeState?: (hasChanges: boolean) => void;
  onLocalChange?: (parameters: ParameterData) => void; // Notifica o pai sobre alterações em tempo real
}

export const AgentParameters: React.FC<ParametersProps> = ({ parameters, onUpdate, onChangeState, onLocalChange }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.split('-')[0]; // 'pt' ou 'en'
  
  // Traduzir parameters fields
  const translatedParameters = useMemo(() => {
    if (!parameters) return null;
    return {
      ...parameters,
      fields: parameters.fields ? translateDeep(parameters.fields, currentLang) : []
    };
  }, [parameters, currentLang]);
  
  const [parameterValues, setParameterValues] = useState<{ [key: string]: ParameterValue }>({});
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const isInitialMount = useRef(true); // Flag para detectar montagem inicial

  useEffect(() => {
    if (translatedParameters?.fields) {
      const initialValues: { [key: string]: ParameterValue } = {};
      translatedParameters.fields.forEach((field) => {
        // Try to get value from translatedParameters.values first, then field.value, then default
        initialValues[field.name] = 
          (translatedParameters.values && translatedParameters.values[field.name] !== undefined) ? translatedParameters.values[field.name] :
          (field.value !== undefined) ? field.value :
          (field.default !== undefined) ? field.default : '';
      });
      setParameterValues(initialValues);
      setHasLocalChanges(false);
      
      // Após carregar valores iniciais, marcar que não é mais montagem inicial
      setTimeout(() => {
        isInitialMount.current = false;
      }, 300);
    }
  }, [translatedParameters]);
  
  // Adiciona um efeito para notificar o componente pai quando houver alterações
  useEffect(() => {
    if (hasLocalChanges && !isInitialMount.current) {
      onChangeState?.(true);
    }
  }, [hasLocalChanges, onChangeState]);

  const handleValueChange = (name: string, value: ParameterValue) => {
    const newValues = {
      ...parameterValues,
      [name]: value
    };
    setParameterValues(newValues);
    setHasLocalChanges(true);
    
    // Notifica o pai sobre a alteração em tempo real para manter estado entre trocas de aba
    if (onLocalChange && translatedParameters?.fields) {
      const updatedFields = translatedParameters.fields.map(field => ({
        ...field,
        value: field.name === name ? value : newValues[field.name]
      }));
      onLocalChange({
        fields: updatedFields,
        values: newValues
      });
    }
  };
  
  // Nova função para salvar os dados quando o botão Save for clicado
  // Esta função será chamada pelo componente pai através do ref
  useEffect(() => {
    // Expõe a função de salvar para o componente pai
    if (onUpdate && typeof onUpdate === 'function') {
      // Primeiro, definimos a função saveParameters
      const saveParametersFunc = () => {
        if (!hasLocalChanges) {
          return { success: true, skipped: true };
        }
        
        if (!translatedParameters?.fields) {
          return false;
        }
        
        // Coletar todos os campos obrigatórios ausentes
        const missingRequiredFields = translatedParameters.fields
          .filter(field => field.required)
          .filter(field => {
            const value = parameterValues[field.name];
            const isEmpty = (value === undefined || value === null || value === '') && (value as ParameterValue) !== 0;
            return isEmpty;
          });

        if (missingRequiredFields.length === 0) {
          // Create a new fields array with values embedded in each field
          const updatedFields = translatedParameters.fields.map(field => ({
            ...field,
            value: parameterValues[field.name]
          }));
          
          // Atualiza os parâmetros no componente pai
          onUpdate({
            fields: updatedFields,
            // Keep values object for backward compatibility
            values: parameterValues
          });

          // Marca como não tendo mais alterações locais não salvas neste componente
          setHasLocalChanges(false);
          return { success: true };
        }
        
        return { 
          success: false, 
          missingFields: missingRequiredFields.map(field => field.label || field.name)
        };
      };
      
      // Agora enviamos a função para o componente pai
      onUpdate({
        saveParameters: saveParametersFunc
      });
    }
    
    // Cleanup function
    return () => {
      if (onUpdate && typeof onUpdate === 'function') {
        onUpdate({
          saveParameters: null
        });
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translatedParameters, parameterValues, onUpdate, onChangeState]);

  // If no parameters are defined, show placeholder
  if (!translatedParameters || !translatedParameters.fields || translatedParameters.fields.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{t('agents.settings.parameters.noParametersTitle')}</AlertTitle>
        <AlertDescription>
          {t('agents.settings.parameters.noParametersDescription')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">{t('agents.settings.parameters.title')}</h3>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-6">
            {translatedParameters.fields.map((field) => (
              <div key={field.name} className="grid gap-2">
                <Label htmlFor={field.name} className="flex items-center gap-2">
                  {field.label}
                  {field.required && (
                    <span className="text-red-500 text-xs">*</span>
                  )}
                </Label>

                {field.type === 'string' && (
                  <Input
                    id={field.name}
                    value={(typeof parameterValues[field.name] === 'string' || typeof parameterValues[field.name] === 'number' ? parameterValues[field.name] : '') as string | number}
                    onChange={(e) => handleValueChange(field.name, e.target.value)}
                    placeholder={field.label}
                  />
                )}

                {field.type === 'number' && (
                  <Input
                    id={field.name}
                    type="number"
                    value={(typeof parameterValues[field.name] === 'string' || typeof parameterValues[field.name] === 'number' ? parameterValues[field.name] : '') as string | number}
                    onChange={(e) => handleValueChange(field.name, parseFloat(e.target.value) || 0)}
                    placeholder={field.label}
                  />
                )}

                {field.type === 'select' && field.options && (
                  <Select
                    value={(typeof parameterValues[field.name] === 'string' ? parameterValues[field.name] : '') as string}
                    onValueChange={(value) => handleValueChange(field.name, value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={field.label} />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options.map((option) => {
                        // Support both object format {label, value} and string format
                        const optionValue = typeof option === 'string' ? option : option.value;
                        const optionLabel = typeof option === 'string' ? option : option.label;
                        
                        return (
                          <SelectItem key={optionValue} value={optionValue}>
                            {optionLabel}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}

                {field.type === 'boolean' && (
                  <div className="flex items-center space-x-2">
                    <Switch 
                      id={field.name}
                      checked={!!parameterValues[field.name]}
                      onCheckedChange={(checked) => handleValueChange(field.name, checked)}
                    />
                    <Label htmlFor={field.name} className="font-normal text-sm text-muted-foreground">
                      {parameterValues[field.name] ? t('agents.settings.parameters.enabled') : t('agents.settings.parameters.disabled')}
                    </Label>
                  </div>
                )}

                {field.type === 'json' && (
                  <Textarea 
                    id={field.name}
                    value={(typeof parameterValues[field.name] === 'object'
                      ? JSON.stringify(parameterValues[field.name], null, 2)
                      : typeof parameterValues[field.name] === 'string' || typeof parameterValues[field.name] === 'number' ? parameterValues[field.name] : '') as string | number}
                    onChange={(e) => {
                      try {
                        const value = e.target.value.trim() 
                          ? JSON.parse(e.target.value) 
                          : '';
                        handleValueChange(field.name, value);
                      } catch (err) {
                        // Allow raw input even if not valid JSON yet
                        handleValueChange(field.name, e.target.value);
                      }
                    }}
                    className="min-h-[150px] font-mono text-sm"
                    placeholder="{ ... }"
                  />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
